/**
 * React Query hooks for XLR8 Admin Console
 *
 * Custom hooks that provide data fetching, caching, and state management
 * for the admin console using React Query.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminApiService } from "../services/api";
import {
  BetaUserActivity,
  CreateBetaUserForm,
  GenerateInviteForm,
  TableFilters,
} from "../types";

// Query keys for consistent caching
export const QUERY_KEYS = {
  dashboard: ["admin", "dashboard"] as const,
  betaUsers: (filters?: TableFilters) =>
    ["admin", "betaUsers", filters] as const,
  betaUser: (id: string) => ["admin", "betaUser", id] as const,
  activities: (filters?: TableFilters) =>
    ["admin", "activities", filters] as const,
} as const;

// =============================================================================
// DASHBOARD HOOKS
// =============================================================================

export function useAdminDashboard() {
  return useQuery({
    queryKey: QUERY_KEYS.dashboard,
    queryFn: AdminApiService.getDashboard,
    refetchInterval: 60000, // Refresh every minute (reduced from 30s)
    staleTime: 30000, // Consider data stale after 30 seconds (increased from 10s)
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

// =============================================================================
// BETA USERS HOOKS
// =============================================================================

export function useBetaUsers(filters: TableFilters = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.betaUsers(filters),
    queryFn: () => AdminApiService.getBetaUsers(filters),
    staleTime: 60000, // Keep data fresh for 1 minute (increased from 20s)
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}

export function useBetaUser(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.betaUser(id),
    queryFn: () => AdminApiService.getBetaUser(id),
    enabled: !!id, // Only run query if ID is provided
    staleTime: 2 * 60 * 1000, // Keep fresh for 2 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
}

export function useCreateBetaUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: CreateBetaUserForm) =>
      AdminApiService.createBetaUser(userData),
    onSuccess: () => {
      // Invalidate and refetch beta users list
      queryClient.invalidateQueries({ queryKey: ["admin", "betaUsers"] });
      // Invalidate dashboard to update stats
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboard });
    },
  });
}

export function useUpdateBetaUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      userData,
    }: {
      id: string;
      userData: Partial<CreateBetaUserForm>;
    }) => AdminApiService.updateBetaUser(id, userData),
    onSuccess: (updatedUser) => {
      // Update the specific user in cache
      queryClient.setQueryData(
        QUERY_KEYS.betaUser(updatedUser.id),
        updatedUser,
      );
      // Invalidate users list to show updates
      queryClient.invalidateQueries({ queryKey: ["admin", "betaUsers"] });
    },
  });
}

export function useDeleteBetaUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => AdminApiService.deleteBetaUser(id),
    onSuccess: () => {
      // Invalidate all beta user queries
      queryClient.invalidateQueries({ queryKey: ["admin", "betaUsers"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboard });
    },
  });
}

// =============================================================================
// INVITE TOKEN HOOKS
// =============================================================================

export function useGenerateInviteToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      options,
    }: {
      userId: string;
      options?: GenerateInviteForm;
    }) => AdminApiService.generateInviteToken(userId, options),
    onSuccess: (newToken, { userId }) => {
      // Update the specific user to show new invite status
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.betaUser(userId) });
      // Refresh users list to show updated invite status
      queryClient.invalidateQueries({ queryKey: ["admin", "betaUsers"] });
    },
  });
}

// =============================================================================
// ACTIVITIES HOOKS
// =============================================================================

export function useBetaUserActivities(filters: TableFilters = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.activities(filters),
    queryFn: () => AdminApiService.getBetaUserActivities(filters),
    refetchInterval: 60000, // Refresh every minute for activity updates
    staleTime: 30000, // Consider data stale after 30 seconds
  });
}

export function useMarkActivityPraised() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: number) =>
      AdminApiService.markActivityPraised(activityId),
    onSuccess: () => {
      // Invalidate activities list to show updated praise status
      queryClient.invalidateQueries({ queryKey: ["admin", "activities"] });
      // Update dashboard to reflect changes in pending activities count
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboard });
    },
  });
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

export function useHealthCheck() {
  return useQuery({
    queryKey: ["admin", "health"],
    queryFn: AdminApiService.healthCheck,
    refetchInterval: 300000, // Check every 5 minutes
    staleTime: 60000, // Consider stale after 1 minute
    retry: 3, // Retry failed health checks
  });
}

// Custom hook for optimistic updates when marking activities as praised
export function useOptimisticActivityPraise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: number) =>
      AdminApiService.markActivityPraised(activityId),
    onMutate: async (activityId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["admin", "activities"] });

      // Snapshot the previous value
      const previousActivities = queryClient.getQueryData([
        "admin",
        "activities",
      ]);

      // Optimistically update to the new value
      queryClient.setQueriesData(
        { queryKey: ["admin", "activities"] },
        (old: BetaUserActivity[] | undefined) => {
          if (!old) return old;
          return old.map((activity) =>
            activity.id === activityId
              ? {
                  ...activity,
                  is_praised: true,
                  praised_at: new Date().toISOString(),
                }
              : activity,
          );
        },
      );

      return { previousActivities };
    },
    onError: (err, activityId, context) => {
      // Rollback on error
      if (context?.previousActivities) {
        queryClient.setQueryData(
          ["admin", "activities"],
          context.previousActivities,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ["admin", "activities"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboard });
    },
  });
}

// Hook to get real-time stats for the dashboard
export function useRealTimeStats() {
  const dashboard = useAdminDashboard();
  const activities = useBetaUserActivities({ praised: false });

  return {
    stats: dashboard.data,
    pendingActivities: activities.data?.length || 0,
    isLoading: dashboard.isLoading || activities.isLoading,
    error: dashboard.error || activities.error,
    refetch: () => {
      dashboard.refetch();
      activities.refetch();
    },
  };
}
